import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount, waitForItPromise } from 'src/lib/utils';
import popups from 'src/lib/ui/config/popups';
import { animateCoins } from '../animation/CurrencyAnimation';
import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { trackPinchConversion } from 'src/lib/analytics/events';
import { track } from 'src/lib/analytics/utils';

import { AssetGroup } from '@play-co/timestep-core';

// Group Care Items in pairs so we can reuse existing scroll components
type CloseHandler = () => void;

/**
 * B-coins shop popup
 */
export default class PopupCoinsExchanged extends PopupBasic {
  private ok: ButtonScaleViewWithText;
  private close: CloseHandler;
  private received: (amount: number) => void;
  private coin!: ImageView;

  /**
   * Popup constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 410,
      darkerBg: true,
      skipTitle: true,
      tracking: {
        feature: 'coins-shop',
        $subFeature: 'coins-exchanged-popup',
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.ok = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 338,
      width: 228,
      height: 95,
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      tracking: { feature: 'coins-shop', $subFeature: 'coins-exchanged-ok' },
    });

    this.message.updateOpts({
      x: this.box.style.width / 2,
      y: 107,
      width: 418,
      height: 54,
      size: 40,
      color: '#BF8147',
      centerOnOrigin: true,
    });

    this.createTitle();
    this.received = this.exchangedView();
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/shop/icons/bpoint_header.png',
      'assets/ui/shop/frames/panel_unit.png',
      'assets/ui/shop/icons/bpoint_large.png',
    ]);
  }

  /**
   * Create title
   */
  private createTitle() {
    const banner = new ImageScaleView({
      ...uiConfig.popups.titleBanner,
      superview: this.box,
      x: this.box.style.width / 2,
      centerOnOrigin: true,
    });

    new LangBitmapFontTextView({
      ...uiConfig.popups.title,
      superview: banner,
      x: 86,
      y: 86,
      size: 32,
      width: 307,
      font: bitmapFonts('Body'),
      localeText: () => i18n('currencyExchange.coinsExchanged.title'),
      letterSpacing: -2,
    });

    new ImageView({
      superview: banner,
      image: 'assets/ui/shop/icons/bpoint_header.png',
      width: 60,
      height: 60,
      x: 27,
      y: 73,
    });
  }

  /**
   * On dialog init
   */
  public init(args: { items: number; bonus: number; price: number }) {
    super.init({
      message: i18n('currencyExchange.coinsExchanged.message'),
    });

    // Update UI
    this.received(args.items + args.bonus);

    // Enable from previous dialog show
    this.ok.setDisabled(false);

    // Create on OK click event
    this.ok.onClick = async () => {
      this.ok.setDisabled(true);
      sounds.sfx.play('ok');
      await this.exchange(args.items, args.bonus, args.price);
      this.close();
    };
  }

  /**
   * Exchanged view
   */
  private exchangedView() {
    new ImageScaleView({
      tag: 'underline',
      superview: this.box,
      ...popups.underline,
      width: 426,
      height: 4,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 134,
    });

    const priceFrame = new ImageScaleView({
      tag: 'priceFrame',
      superview: this.box,
      image: 'assets/ui/shop/frames/panel_unit.png',
      scaleMethod: '9slice',
      width: 355,
      height: 102,
      x: this.box.style.width / 2,
      y: 216,
      centerOnOrigin: true,
      sourceSlices: {
        horizontal: { left: 24, right: 24 },
        vertical: { top: 24, bottom: 24 },
      },
    });

    const priceShadow = new LangBitmapFontTextView({
      tag: 'priceShadow',
      superview: priceFrame,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#000000',
      opacity: 0.25,
      wordWrap: false,
      x: 109,
      y: 24,
      size: 49,
      width: 228,
      height: 59,
      font: bitmapFonts('Body'),
      localeText: () => `x `,
      letterSpacing: -2,
    });

    const price = new LangBitmapFontTextView({
      tag: 'price',
      superview: priceFrame,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#F1F5EF',
      wordWrap: false,
      x: 109,
      y: 24 - 4,
      size: 49,
      width: 228,
      height: 59,
      font: bitmapFonts('Body'),
      localeText: () => `x 0`,
      letterSpacing: -2,
    });

    this.coin = new ImageView({
      tag: 'coin',
      superview: priceFrame,
      image: 'assets/ui/shop/icons/bpoint_large.png',
      width: 80,
      height: 80,
      x: 15,
      y: 12,
    });

    return (amount: number) => {
      priceShadow.localeText = () => `x ${parseAmount(amount)}`;
      price.localeText = () => `x ${parseAmount(amount)}`;
    };
  }

  /**
   * Action code on ok button
   */
  private async exchange(items: number, bonus: number, price: number) {
    // How many cons we received
    const received = items + bonus;

    // Animation
    const iconPos = this.coin.getPosition(this.root);
    const start = {
      x: iconPos.x + this.coin.style.width / 2,
      y: iconPos.y + this.coin.style.height / 2,
    };

    // Start coin animation
    const animation = animateCoins({
      superview: this.root,
      start: { position: start, scale: 1 },
      count: Math.min(received, 1000),
    });

    // Wait some time to sync animation and currency change
    await waitForItPromise(700);

    // Exchange gems
    try {
      await StateObserver.invoke.exchangeGemsToPoints(items);

      trackPinchConversion({
        action: 'in_game',
        feature: 'currency_exchange',
        subFeature: 'bPoints',
        resource: 'bPoints',
      });
    } catch (e: any) {
      await openPopupPromise('popupError', {
        title: i18n('basic.error'),
        message: e.message ?? e,
      });
      return;
    }

    // Wait for animation completes
    await animation;

    // Send analytics
    track('CurrencyExchangeFinish', {
      currencyPaid: 'gems',
      currencyEarned: 'b_point',
      amountPaid: price,
      amountEarned: received,
    });
  }
}
